import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookies, setCookies] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allCookies: CookieSettings = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setCookies(allCookies);
    saveConsent(allCookies);
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: CookieSettings = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setCookies(necessaryOnly);
    saveConsent(necessaryOnly);
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    saveConsent(cookies);
    setIsVisible(false);
    setShowSettings(false);
  };

  const saveConsent = (settings: CookieSettings) => {
    localStorage.setItem('cookie-consent', JSON.stringify(settings));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    // Here you would typically initialize the actual cookies/scripts
    // based on the user's consent
    initializeCookies(settings);
  };

  const initializeCookies = (settings: CookieSettings) => {
    // This is where you would initialize actual tracking scripts
    // For example: Google Analytics, Facebook Pixel, etc.
    
    if (settings.analytics) {
      // Initialize Google Analytics or other analytics
      console.log('Analytics cookies enabled');
    }
    
    if (settings.marketing) {
      // Initialize marketing cookies
      console.log('Marketing cookies enabled');
    }
    
    if (settings.functional) {
      // Initialize functional cookies
      console.log('Functional cookies enabled');
    }
    
    // Necessary cookies are always enabled
    console.log('Necessary cookies enabled');
  };

  const updateCookieSetting = (type: keyof CookieSettings, value: boolean) => {
    setCookies(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 shadow-2xl">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {!showSettings ? (
          // Simple Banner View
          <div className="py-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
              <div className="flex-1 max-w-3xl">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-lg"></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Datenschutz & Cookies
                  </h3>
                </div>
                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                  Wir setzen technisch notwendige Cookies ein, um den ordnungsgemäßen Betrieb unserer Website zu gewährleisten (§ 25 Abs. 2 TDDDG). Zusätzliche Cookies für statistische Analysen oder Marketingzwecke nutzen wir nur mit Ihrer ausdrücklichen Einwilligung (§ 25 Abs. 1 TDDDG i. V. m. Art. 6 Abs. 1 lit. a DSGVO). Weitere Details finden Sie in unserer{' '}
                  <Link to="/datenschutz" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline font-medium">
                    Datenschutzerklärung
                  </Link>
                  {' '}sowie in den{' '}
                  <Link to="/cookie-einstellungen" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline font-medium">
                    Cookie-Einstellungen
                  </Link>.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-6 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200 font-medium text-sm"
                >
                  <i className="ri-settings-3-line mr-2"></i>
                  Einstellungen
                </button>
                <button
                  onClick={handleAcceptNecessary}
                  className="px-6 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200 font-medium text-sm"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-emerald-500/25"
                >
                  <i className="ri-check-line mr-2"></i>
                  Alle akzeptieren
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Detailed Settings View
          <div className="py-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                Cookie-Einstellungen anpassen
              </h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Necessary Cookies */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                      <i className="ri-lock-line text-emerald-600 dark:text-emerald-400"></i>
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white">Notwendige Cookies</h4>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={cookies.necessary}
                      disabled
                      className="sr-only"
                    />
                    <div className="w-12 h-6 bg-emerald-500 rounded-full cursor-not-allowed opacity-75"></div>
                    <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform transform translate-x-6"></div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Diese Cookies sind für den Betrieb der Website unerlässlich und können nicht deaktiviert werden. 
                  Sie ermöglichen grundlegende Funktionen wie Seitennavigation und Zugriff auf sichere Bereiche.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <i className="ri-bar-chart-line text-blue-600 dark:text-blue-400"></i>
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white">Analytics Cookies</h4>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cookies.analytics}
                      onChange={(e) => updateCookieSetting('analytics', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                  indem Informationen anonym gesammelt und gemeldet werden.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <i className="ri-megaphone-line text-purple-600 dark:text-purple-400"></i>
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white">Marketing Cookies</h4>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cookies.marketing}
                      onChange={(e) => updateCookieSetting('marketing', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Diese Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen, 
                  um relevante Anzeigen zu schalten und Marketingkampagnen zu personalisieren.
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <i className="ri-settings-3-line text-orange-600 dark:text-orange-400"></i>
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white">Funktionale Cookies</h4>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cookies.functional}
                      onChange={(e) => updateCookieSetting('functional', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Diese Cookies ermöglichen erweiterte Funktionen wie Video-Wiedergabe, 
                  Kommentare und das Teilen von Inhalten über soziale Medien.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0 pt-6 border-t border-gray-200 dark:border-slate-700">
              <div className="text-sm text-slate-600 dark:text-slate-300">
                <i className="ri-information-line mr-2"></i>
                Ihre Einstellungen können jederzeit in unserem{' '}
                <Link to="/datenschutz" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline font-medium">
                  Datenschutzcenter
                </Link>
                {' '}geändert werden.
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200 font-medium text-sm"
                >
                  Zurück
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-emerald-500/25"
                >
                  <i className="ri-save-line mr-2"></i>
                  Einstellungen speichern
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
